import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useAuth } from 'contexts/AuthContext';
import {
  BookOpen, ArrowRight, Sparkles, UserPlus,
  Play, Zap,
} from 'lucide-react';
import { toast } from 'sonner';
import CircleWidget from './CircleWidget';


/* ─── GLOBAL STYLES ─────────────────────────────────────────────────────── */
const GLOBAL_STYLES = `
  @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600;700;800&display=swap');

  @keyframes fadeUp   { from { opacity:0; transform:translateY(20px); } to { opacity:1; transform:translateY(0); } }
  @keyframes fadeIn   { from { opacity:0 } to { opacity:1 } }
  @keyframes pulse    { 0%,100%{opacity:1} 50%{opacity:0.4} }
  @keyframes scanLine { 0%{transform:translateY(-100%)} 100%{transform:translateY(100vh)} }

  .dash-root {
    display: flex;
    width: 100%;
    height: 100%;
    max-height: 100%;
    overflow: hidden;
    font-family: var(--org-font, 'DM Sans', sans-serif);

    animation: fadeIn 0.4s ease;
    position: relative;
  }



  .dash-root::before {
    content: '';
    position: fixed;
    top: 0; left: 0; right: 0;
    height: 2px;
    background: linear-gradient(90deg, transparent, var(--org-accent, rgba(0,255,255,0.5)), transparent);
    animation: scanLine 8s linear infinite;
    z-index: 1;
    pointer-events: none;
  }

  .main {
    flex: 1;
    overflow-y: auto;
    overflow-x: hidden;
    position: relative;
    z-index: 2;
    scrollbar-width: thin;
    scrollbar-color: rgba(0,255,255,0.2) transparent;
  }
  .main::-webkit-scrollbar { width: 4px; }
  .main::-webkit-scrollbar-track { background: transparent; }
  .main::-webkit-scrollbar-thumb { background: rgba(0,255,255,0.2); border-radius: 4px; }

  .hero-section {
    position: relative;
    padding: 16px 32px 0;
    z-index: 5;
  }
  .greeting-text {
    font-size: 32px;
    font-weight: 300;
    color: var(--org-accent, #ccff00);
    letter-spacing: -1px;
    line-height: 1.05;
    text-shadow: 0 0 20px var(--org-accent, rgba(204,255,0,0.4));
  }

  .card {
    background: rgba(10, 8, 30, 0.35);
    backdrop-filter: blur(15px) saturate(160%);
    -webkit-backdrop-filter: blur(15px) saturate(160%);
    border-radius: 18px;
    overflow: hidden;
    animation: fadeUp 0.5s ease both;
    transition: all 0.3s ease;
  }

  .card-cyan {
  border: 2px solid rgb(var(--brand-primary-rgb, 0 255 255) / 0.6);
  box-shadow: 0 0 20px rgb(var(--brand-primary-rgb, 0 255 255) / 0.25);
}
.card-cyan:hover {
  border-color: var(--brand-primary, #00FFFF);
  box-shadow: 0 0 30px rgb(var(--brand-primary-rgb, 0 255 255) / 0.45),
              inset 0 0 30px rgb(var(--brand-primary-rgb, 0 255 255) / 0.2);
}
.card-magenta { 
  border: 2px solid rgb(var(--brand-secondary-rgb, 255 115 0) / 0.6); 
}
.card-magenta:hover {
  border-color: var(--brand-secondary, #FF7300);
  box-shadow: 0 0 30px rgb(var(--brand-secondary-rgb, 255 115 0) / 0.45),
              inset 0 0 30px rgb(var(--brand-secondary-rgb, 255 115 0) / 0.2);
}

  .content-grid {
    position: relative;
    z-index: 5;
    padding: 12px 32px 16px;
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 14px;
  }

  .continue-card-header {
    padding: 14px 18px 10px;
    font-size: 11px;
    font-weight: 800;
    color:rgb(var(--brand-text-rgb) /0.7);
    text-transform: uppercase;
    letter-spacing: 0.14em;
  }

  .video-thumb {
    position: relative;
    height: 130px; 
    margin: 0 14px;
    border-radius: 12px;
    overflow: hidden;
    cursor: pointer;
    border: 1px solid rgba(0,255,255,0.3);
    background-image: 
      repeating-linear-gradient(0deg, transparent, transparent 40px, rgba(0,255,255,0.05) 40px, rgba(0,255,255,0.05) 41px),
      repeating-linear-gradient(90deg, transparent, transparent 40px, rgba(0,255,255,0.05) 40px, rgba(0,255,255,0.05) 41px);
    background-color: #061121;
  }
  
  .video-thumb-bg {
    position: absolute; inset: 0;
    background: radial-gradient(circle at 50% 50%, rgba(0,255,255,0.05) 0%, transparent 60%);
    pointer-events: none;
  }
  
  .video-overlay-gradient {
    position: absolute; inset: 0;
    background: linear-gradient(to top, rgba(0,20,30,0.9) 0%, rgba(0,20,30,0.1) 60%, transparent 100%);
  }
  .video-meta { position: absolute; bottom: 0; left: 0; right: 0; padding: 16px; z-index: 10; }
  .video-meta-sub { font-size: 10px; color: rgb(var(--brand-text-rgb) /0.6); margin-bottom: 4px; letter-spacing: 0.06em; }
  .video-meta-title { font-size: 16px; font-weight: 700; color: var(--brand-text); line-height: 1.3; }

  .play-circle {
    position: absolute; top: 50%; left: 50%;
    transform: translate(-50%,-50%);
    width: 52px; height: 52px;
    background: rgba(0,255,255,0.1);
    border: 2px solid rgba(0,255,255,0.5);
    border-radius: 50%;
    display: flex; align-items: center; justify-content: center;
    cursor: pointer; transition: all 0.2s;
    backdrop-filter: blur(8px);
    z-index: 20;
    box-shadow: 0 0 20px rgba(0,255,255,0.3);
  }
  .play-circle:hover {
    transform: translate(-50%,-50%) scale(1.1);
    background: rgba(0,255,255,0.3);
    border-color: var(--brand-primary, #00FFFF);
    box-shadow: 0 0 30px rgba(0,255,255,0.6);
  }
  .play-circle svg { color: var(--brand-text); margin-left: 4px; }

  .card-actions { display: flex; gap: 10px; padding: 14px 14px; }

  .btn-primary-neon {
    background: var(--org-accent, #00e1ff);
    color: #000;
    border: none; border-radius: 8px;
    font-size: 12px; font-weight: 800; padding: 10px 20px; cursor: pointer;
    font-family: 'DM Sans', sans-serif; transition: all 0.2s;
    text-decoration: none; display: inline-block;
    box-shadow: 0 0 15px rgba(204,255,0,0.4);
  }
  .btn-primary-neon:hover { background: #33ffd6; transform: translateY(-1px); box-shadow: 0 0 25px rgba(0, 204, 255, 0.6); }

  .btn-ghost-neon {
    background: transparent;
    border: 1px solid var(--org-accent, #00a2ff);
    color: var(--org-accent, #00fff2);
    border-radius: 8px; font-size: 12px; font-weight: 700;
    padding: 10px 20px; cursor: pointer; font-family: 'DM Sans', sans-serif;
    text-decoration: none; display: inline-block; transition: all 0.2s;
  }
  .btn-ghost-neon:hover { background: rgba(0, 255, 221, 0.1); box-shadow: 0 0 15px rgba(0, 255, 242, 0.2); }

  .module-cover-card { position: relative; overflow: hidden; padding: 16px; }
  .module-cover-gradient {
  position: absolute; 
  inset: 0;
  background: radial-gradient(circle at top right, rgb(var(--brand-secondary-rgb, 255 115 0) / 0.15) 0%, transparent 60%);
  pointer-events: none;
}
  
  .module-cover-tag-magenta {
  display: inline-block;
  padding: 3px 10px;
  border-radius: 6px;
  font-size: 10px;
  font-weight: 800;
  letter-spacing: 0.1em;
  margin-bottom: 8px;
  background: rgb(var(--brand-secondary-rgb, 255 115 0) / 0.15); 
  border: 1px solid rgb(var(--brand-secondary-rgb, 255 115 0) / 0.4);
  color: var(--brand-secondary, #FF7300);
}
  
  .module-cover-title {
  font-size: 18px; 
  font-weight: 800; 
  color: var(--brand-text);
  line-height: 1.25; 
  margin-bottom: 4px;
  text-shadow: 0 0 15px rgb(var(--brand-secondary-rgb, 255 115 0) / 0.3);
}
  .module-cover-sub { font-size: 12px; color: rgb(var(--brand-text-rgb) /0.5); margin-bottom: 14px; }
  
  .module-cover-prog-label {
    display: flex; justify-content: space-between;
    font-size: 11px; color: rgb(var(--brand-text-rgb) /0.7); margin-bottom: 6px;
  }
  .module-cover-prog-label span:last-child { color: var(--brand-secondary, #FF7300); font-weight: 800; }
  
  .module-cover-track-magenta { 
  background: rgb(var(--brand-secondary-rgb, 255 115 0) / 0.1); 
  border: 1px solid rgb(var(--brand-secondary-rgb, 255 115 0) / 0.3);
  border-radius: 8px; 
  height: 10px; 
  overflow: hidden; 
  box-shadow: inset 0 0 10px rgba(0,0,0,0.5);
  margin-bottom: 14px;
}
  
  .module-cover-fill-magenta {
    height: 100%;
    background-color: var(--brand-secondary, #FF7300);
    box-shadow: 0 0 15px var(--brand-secondary, #FF7300);
    border-radius: 8px;
    transition: width 0.5s ease;
  }

  .circle-label {
    font-size: 11px; font-weight: 700; color:rgb(var(--brand-text-rgb) /0.5);
    letter-spacing: 0.16em; text-transform: uppercase;
    margin-top: 0; margin-bottom: 10px; padding-left: 2px;
    display: flex; align-items: center; gap: 6px;
  }

  .module-top-user {
  display: flex; 
  align-items: center; 
  gap: 10px; 
  padding: 6px 12px;
  background: rgb(var(--brand-secondary-rgb, 255 115 0) / 0.05);
  border: 1px solid rgb(var(--brand-secondary-rgb, 255 115 0) / 0.2);
  border-radius: 10px;
  backdrop-filter: blur(12px);
  margin-top: 8px;
}
  .module-top-user-info { flex: 1; }
  .module-top-user-name { font-size: 12px; font-weight: 700; color: var(--brand-text); }
  .module-top-user-meta { font-size: 10px; color: rgb(var(--brand-text-rgb) /0.5); }
.module-top-user-xp { 
  font-size: 13px; 
  font-weight: 800; 
  color: var(--brand-secondary, #FF7300); 
  text-shadow: 0 0 10px rgb(var(--brand-secondary-rgb, 255 115 0) / 0.4); 
}
  .loading-screen {
    display: flex; align-items: center; justify-content: center;
    height: 100vh; background: #060810;
    color: rgba(0,255,255,0.8); font-size: 16px; font-weight: 700;
    font-family: 'DM Sans', sans-serif; animation: pulse 1.5s infinite;
    text-shadow: 0 0 10px rgba(0,255,255,0.5);
  }
`;

/* ─── AVATAR ─────────────────────────────────────────────────────────────── */
const AVATAR_COLORS = [
  'var(--brand-primary, #00FFFF)','var(--brand-secondary, #FF7300)','#ccff00','#a855f7','#3b82f6',
  '#f43f5e','#10b981','#f59e0b','#ef4444','#8b5cf6',
];
const getInitials = (name = '') =>
  name.split(' ').slice(0,2).map(w => w[0]?.toUpperCase() || '').join('') || '?';

const UserAvatar = ({ user: u, size = 42, style = {} }) => {
  const [imgError, setImgError] = useState(false);
  const initials = getInitials(u?.name || u?.full_name || '');
  const color = AVATAR_COLORS[Math.abs((u?.id || 0) % AVATAR_COLORS.length)];
  if (u?.avatar_url && !imgError) {
    return (
      <img src={u.avatar_url} alt={u.name} onError={() => setImgError(true)}
        style={{ width: size, height: size, borderRadius: '10px', objectFit: 'cover', border: '1px solid rgba(255,255,255,0.2)', ...style }} />
    );
  }
  return (
    <div style={{
      width: size, height: size, borderRadius: '10px', background: color,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontWeight: 800, fontSize: size * 0.35, color: '#000', flexShrink: 0,
      boxShadow: `0 0 15px ${color}66`, ...style,
    }}>
      {initials}
    </div>
  );
};

/* ─── DASHBOARD ─────────────────────────────────────────────────────────── */
const Dashboard = () => {
  const { user, api } = useAuth();
const organization = user?.organization || null;


  const [searchParams] = useSearchParams();
  const [stats, setStats]                       = useState(null);
  const [continueWatching, setContinueWatching] = useState(null);
  const [assignedModules, setAssignedModules]   = useState([]);
  const [circleMode, setCircleMode] = useState('all');
  const [circleLeader, setCircleLeader] = useState(null); 
  const [circleUsers, setCircleUsers]           = useState([]);
  const [loading, setLoading]                   = useState(true);
  const [currentOrgId, setCurrentOrgId]         = useState(searchParams.get('orgId') || null);


  useEffect(() => { fetchDashboardData(currentOrgId); }, [currentOrgId]);

  const fetchDashboardData = async (orgId = null) => {
    setLoading(true);
    try {
      const params = {};
      if (orgId) params.organization_id = orgId;
      const [statsRes, continueRes, modulesRes] = await Promise.all([
        api.get('/stats/dashboard', { params }),
        api.get('/progress/continue', { params }),
        api.get('/modules/assigned'),
      ]);

      setStats(statsRes.data);
      try {
  const c = await api.get('/users/circle');
  setCircleUsers(c.data?.users || []);
  setCircleMode(c.data?.mode || 'all');
  setCircleLeader(c.data?.leader || null);
} catch {}

      setAssignedModules(modulesRes.data || []);
    } catch { toast.error('No se pudieron cargar los datos del dashboard'); }
    finally { setLoading(false); }
  };

  const completedModules  = assignedModules.filter(m => m.assignment_status === 'completed');
  const inProgressModules = assignedModules.filter(m => m.assignment_status === 'in_progress');
  const pendingModules    = assignedModules.filter(m => m.assignment_status === 'pending');
  const totalModules      = assignedModules.length;
  const completionRate    = totalModules > 0 ? Math.round((completedModules.length / totalModules) * 100) : 0;

  const currentActiveModule = continueWatching?.course || inProgressModules[0] || pendingModules[0];
  const displayTitle    = continueWatching?.course?.title || currentActiveModule?.title || 'No tienes módulos asignados';
  const displaySubTitle = continueWatching?.skill_path?.title || currentActiveModule?.category || 'Ruta de Aprendizaje';
  const defaultCyberBg  = "https://images.unsplash.com/photo-1550751827-4bd374c3f58b?q=80&w=1080&auto=format&fit=crop";
  const displayImage    = continueWatching?.skill_path?.image_url || currentActiveModule?.image_url || defaultCyberBg;
  const targetLink      = continueWatching?.course?.id 
    ? `/course/${continueWatching.course.id}` 
    : currentActiveModule?.id 
      ? `/module/${currentActiveModule.id}` 
      : '/my-modules';

  const circleMembers = circleUsers
  .filter(u => u.id !== user?.id)
  .slice(0, 12);

  const progressPercent = Math.round(continueWatching?.progress?.progress_percent || completionRate || 0);

  // Obtener URL del logo (si existe)


  if (loading) return (
    <><style>{GLOBAL_STYLES}</style><div className="loading-screen">INICIANDO SISTEMA...</div></>
  );

  return (
    <div
      className="dash-root cyber-font min-h-screen p-8 relative" 
    >
      <style>{GLOBAL_STYLES}</style>
      
      {/* 
        Aseguramos pasar las props de la organización o la URL directamente 
        y un style para garantizar posicionamiento flotante detrás del contenido.
      */}
      
      <div className="main">
        {/* HERO */}
        <section className="hero-section">
          <h1 className="greeting-text">
            Hola, {user?.name?.split(' ')[0] || 'Paula'}
          </h1>
        </section>

        {/* MAIN CARDS GRID */}
        <div className="content-grid">

          {/* TARJETA CIAN: SIGUE APRENDIENDO */}
          <div className="card card-cyan" style={{ animationDelay: '0s' }}>
            <div className="continue-card-header">Sigue aprendiendo</div>

            <div className="video-thumb">
              <div className="video-thumb-bg" />
              {displayImage && (
                <img src={displayImage} alt=""
                  style={{ position:'absolute', inset:0, width:'100%', height:'100%', objectFit:'cover', opacity:0.6 }} />
              )}
              <div className="video-overlay-gradient" />
              <div className="play-circle"><Play size={22} fill="currentColor" /></div>
              
              <div className="video-meta">
                <div className="video-meta-sub">{displaySubTitle}</div>
                <div className="video-meta-title">{displayTitle}</div>
              </div>
            </div>

            <div className="card-actions">
              <Link to={targetLink} className="btn-primary-neon">Ir a la clase</Link>
              <Link to="/my-modules" className="btn-ghost-neon">Todas las clases</Link>
            </div>
          </div>

          {/* TARJETA NARANJA: MÓDULO ACTIVO + CÍRCULO */}
          <div className="card card-magenta module-cover-card" style={{ animationDelay: '0.06s' }}>
            <div className="module-cover-gradient" />
            
            <div style={{ position: 'relative', zIndex: 2 }}>
              <div className="module-cover-tag-magenta">▶ EN PROGRESO</div>
              <div className="module-cover-title">{displayTitle}</div>
              <div className="module-cover-sub">{displaySubTitle}</div>
              
              <div>
                <div className="module-cover-prog-label">
                  <span>Progreso</span>
                  <span>{progressPercent}%</span>
                </div>
                <div className="module-cover-track-magenta">
                  <div
                    className="module-cover-fill-magenta"
                    style={{ width: `${progressPercent}%` }}
                  />
                </div>
              </div>

              {circleMode !== 'none' && (
  <>
    <div className="circle-label">
      <span style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: 'var(--brand-secondary, #FF7300)', display: 'inline-block', boxShadow: '0 0 8px var(--brand-secondary, #FF7300)', flexShrink: 0 }}></span>
      Tu Círculo{circleMode === 'department' ? ' · Mi departamento' : ''}
    </div>

    <div style={{
  background: 'rgba(0,0,0,0.4)',
  border: '1px solid rgb(var(--brand-secondary-rgb, 255 115 0) / 0.3)',
  borderRadius: 12, 
  padding: 16,
  display: 'flex', 
  justify: 'center', 
  alignItems: 'center',
  boxShadow: 'inset 0 0 20px rgb(var(--brand-secondary-rgb, 255 115 0) / 0.05)',
}}>
  <CircleWidget users={circleMembers} currentUser={user} completionRate={progressPercent} />
</div>

    {circleLeader && (
      <div className="module-top-user">
        <UserAvatar user={circleLeader} size={34} />
        <div className="module-top-user-info">
          <div className="module-top-user-name">{circleLeader.name}</div>
          <div className="module-top-user-meta">🏆 Líder del grupo</div>
        </div>
        <div className="module-top-user-xp">{circleLeader.xp || 0} XP</div>
      </div>
    )}
  </>
)}

              
            </div>
          </div>

        </div>
      </div>
    </div>
  );
};

export default Dashboard;